/**
 * Sealed notes (friend of the family): pass a short secret across the table through a
 * Handoff mailbox — Dan's own encrypted, 42-hour, no-accounts drop box
 * (`src/vendor/handoff/`, see `~/Development/handoff/AGENTS.md`).
 *
 * The shape of the secret, and why:
 *
 *  - The author's browser encrypts the note into a fresh mailbox and gets back a **spoken
 *    code** ("the gentle raven doth wake the silver moon"). The words *are* the key: they
 *    derive the mailbox address and the AES key, the server only ever sees ciphertext.
 *  - The chat line that announces the note never carries the code. The match doc is
 *    readable by every spectator in the household, so the words travel out of band — said
 *    out loud, or texted. That is the whole fun of it: "ask them for the magic words".
 *  - Read-only box (`writable: false`): the recipient can open it (up to the mailbox's 25
 *    opens) but nobody can change it. It self-destructs after 42 hours, never extended.
 *
 * Handoff refuses localhost and any origin it doesn't know, so in dev every call fails —
 * which is exactly the path the UI must handle gracefully anyway. The module is loaded
 * with a dynamic `import()` so the 50 KB client (QR code generator included) stays out of
 * the main bundle until somebody actually seals or opens a note.
 */

/** The app name registered for AwwYeah in Handoff's `server/box.php` `APPS`. */
import type * as HandoffTypes from '../../vendor/handoff/handoff-v1.js';

export const SEALED_NOTE_APP = 'awwyeah';
/** Long enough for a real secret, short enough to read off a phone. */
export const SEALED_NOTE_MAX = 500;
const NOTE_KIND = 'awwyeah-note';

export type SealedNoteErrorCode = 'empty' | 'too_long' | 'bad_words' | 'gone' | 'mailbox';

export class SealedNoteError extends Error {
  constructor(
    readonly code: SealedNoteErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'SealedNoteError';
  }
}

export const MAILBOX_DOWN_COPY = 'The note mailbox didn’t answer — try again in a bit.';

export interface SealedNote {
  /** The five code words, in order. */
  words: string[];
  /** How to say them out loud: "the gentle raven doth wake the silver moon". */
  spoken: string;
  /** ms since epoch. */
  expires: number;
}

export interface OpenedNote {
  text: string;
  from: string;
}

/** Trims and bounds a note before it's sealed. Pure. */
export function cleanNoteText(raw: string): string {
  const text = raw.trim();
  if (!text) throw new SealedNoteError('empty', 'Write something first.');
  if (text.length > SEALED_NOTE_MAX) {
    throw new SealedNoteError('too_long', `Keep it under ${SEALED_NOTE_MAX} characters.`);
  }
  return text;
}

/**
 * Validates what came out of a mailbox. It arrived from whoever holds the words, so it is
 * untrusted: wrong kind, wrong types or over-long strings are all "not a note". Rendered as
 * plain text by the caller, never as HTML. Pure.
 */
export function parseNotePayload(data: unknown): OpenedNote | null {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  if (record['kind'] !== NOTE_KIND) return null;
  const text = record['text'];
  const from = record['from'];
  if (typeof text !== 'string' || text.trim().length === 0 || text.length > SEALED_NOTE_MAX) {
    return null;
  }
  const safeFrom = typeof from === 'string' && from.trim() ? from.trim().slice(0, 40) : 'Someone';
  return { text, from: safeFrom };
}

/** The chat line announcing a note. Never contains the code. Pure. */
export function sealedNoteAnnouncement(authorName: string): string {
  return `🔒 ${authorName} passed you a sealed note — ask them for the magic words (self-destructs in 42 hours)`;
}

type HandoffModule = typeof HandoffTypes;

function loadHandoff(): Promise<HandoffModule> {
  return import('../../vendor/handoff/handoff-v1.js');
}

function isGone(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 404 || status === 410;
}

/** Seals `raw` into a fresh read-only mailbox. Throws `SealedNoteError`. */
export async function sealNote(raw: string, fromName: string): Promise<SealedNote> {
  const text = cleanNoteText(raw);
  let mod: HandoffModule;
  try {
    mod = await loadHandoff();
  } catch {
    throw new SealedNoteError('mailbox', MAILBOX_DOWN_COPY);
  }
  try {
    const handoff = new mod.Handoff({ app: SEALED_NOTE_APP });
    const box = await handoff.createSpoken(
      { kind: NOTE_KIND, text, from: fromName.slice(0, 40) },
      { writable: false },
    );
    return { words: box.words, spoken: mod.sayPhrase(box.words), expires: box.expires };
  } catch {
    throw new SealedNoteError('mailbox', MAILBOX_DOWN_COPY);
  }
}

/** Opens a note from the words somebody typed (typos within reason are fixed). */
export async function openSealedNote(typed: string): Promise<OpenedNote> {
  let mod: HandoffModule;
  try {
    mod = await loadHandoff();
  } catch {
    throw new SealedNoteError('mailbox', MAILBOX_DOWN_COPY);
  }
  const parsed = mod.parseAnyPhrase(typed);
  if ('error' in parsed) throw new SealedNoteError('bad_words', parsed.error);

  let data: unknown;
  try {
    const handoff = new mod.Handoff({ app: SEALED_NOTE_APP });
    data = (await handoff.read({ say: parsed.words })).data;
  } catch (error) {
    if (isGone(error)) {
      throw new SealedNoteError(
        'gone',
        'No note behind those words — it may have self-destructed, or a word is off.',
      );
    }
    throw new SealedNoteError('mailbox', MAILBOX_DOWN_COPY);
  }
  const note = parseNotePayload(data);
  if (!note) throw new SealedNoteError('gone', 'Those words open something, but it isn’t a note.');
  return note;
}
